/**
 * /admin/pages — Content Publishing CMS (Feb 2026).
 *
 * Single page that hosts the list view AND the editor. The editor is a
 * lightweight contentEditable surface with a toolbar covering the exact
 * surface the spec promised (headings, formatting, links, lists, blockquote,
 * hr, tables, images, YouTube). A "Source" toggle switches to plain HTML.
 *
 * NOT trying to be a full-blown TinyMCE — the backend sanitizer is the
 * authority; anything unsafe is stripped on save. The editor's job is
 * ergonomic input, not security.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Save, Eye, Archive, GitBranch, Send, Trash2, Bold, Italic, Underline,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImgIcon,
  Table as TableIcon, Youtube, Minus, Code, ArrowLeft, Copy, ExternalLink,
} from 'lucide-react';
import { API } from '@/App';
import { isReservedSlug } from '@/lib/reservedRoutes';
import { PublicPage } from '@/components/cms/PublicRenderers';

// Public URL base used when auto-defaulting the canonical URL and rendering
// the "Public URL" hint under the Slug field. Falls back to the deployed
// app's origin so preview environments still show a valid live-preview URL.
const PUBLIC_BASE_URL = (process.env.REACT_APP_PUBLIC_BASE_URL || 'https://vox.satisfind.com').replace(/\/$/, '');

function publicPathFor(contentType, slug) {
  const s = (slug || '').trim();
  if (!s) return '';
  return contentType === 'article' ? `/blog/${s}` : `/${s}`;
}
function publicUrlFor(contentType, slug) {
  const p = publicPathFor(contentType, slug);
  return p ? `${PUBLIC_BASE_URL}${p}` : '';
}

const CONTENT_TYPES = [{ v: 'page', l: 'Page' }, { v: 'article', l: 'Article' }];
const STATUSES = [
  { v: 'draft', l: 'Draft', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { v: 'published', l: 'Published', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { v: 'archived', l: 'Archived', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
];
const VISIBILITY = [
  { v: 'public', l: 'Public' },
  { v: 'satisfinder', l: 'satisFINDER only' },
  { v: 'client', l: 'Client only' },
  { v: 'authenticated', l: 'Any authenticated user' },
];

function slugify(v) {
  return (v || '').toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// ── List view ────────────────────────────────────────────────────────
export default function AdminPages() {
  const params = useParams();
  const [sp] = useSearchParams();
  if (params.id === 'new' || sp.get('new') === '1') return <PageEditor mode="create" />;
  if (params.id) return <PageEditor mode="edit" contentId={params.id} />;
  return <ListView />;
}

function ListView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [visibility, setVisibility] = useState('all');
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (type !== 'all') p.set('content_type', type);
    if (status !== 'all') p.set('status', status);
    if (visibility !== 'all') p.set('visibility', visibility);
    const r = await fetch(`${API}/admin/cms/content?${p}`, { credentials: 'include' });
    const d = await r.json();
    setRows(d.rows || []); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, status, visibility]);

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-['Montserrat'] text-2xl font-bold">Content Pages</h1>
            <p className="text-sm text-[#64748B]">Pages, articles, legal content and restricted training in one publishing tool.</p>
          </div>
          <Button className="bg-[#1A3E6F] hover:bg-[#12305A] text-white" onClick={() => nav('/admin/pages/new')} data-testid="cms-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New content
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-wrap gap-2 items-center">
          <Input placeholder="Search title or slug…" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && load()} className="max-w-xs" data-testid="cms-search" />
          <Select value={type} onValueChange={setType}><SelectTrigger className="w-[140px]" data-testid="cms-filter-type"><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {CONTENT_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[140px]" data-testid="cms-filter-status"><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent></Select>
          <Select value={visibility} onValueChange={setVisibility}><SelectTrigger className="w-[180px]" data-testid="cms-filter-visibility"><SelectValue /></SelectTrigger><SelectContent>
            <SelectItem value="all">All visibilities</SelectItem>
            {VISIBILITY.map(v => <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>)}
          </SelectContent></Select>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-[#475569]">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Slug</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Visibility</th>
                <th className="text-left px-3 py-2">Version</th>
                <th className="text-left px-3 py-2">Last updated</th>
                <th className="text-left px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-[#94A3B8]">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#94A3B8]" data-testid="cms-empty">No content yet.</td></tr>}
              {rows.map(r => {
                const st = STATUSES.find(s => s.v === r.status);
                return (
                  <tr key={r.content_id} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer"
                      onClick={() => nav(`/admin/pages/${r.content_id}`)} data-testid={`cms-row-${r.slug}`}>
                    <td className="px-3 py-2 font-medium">{r.title}</td>
                    <td className="px-3 py-2 text-[#64748B]">{r.content_type}</td>
                    <td className="px-3 py-2 font-mono text-xs">/{r.content_type === 'article' ? 'blog/' : ''}{r.slug}</td>
                    <td className="px-3 py-2"><Badge className={st?.cls}>{st?.l || r.status}</Badge></td>
                    <td className="px-3 py-2 text-xs">{r.visibility}</td>
                    <td className="px-3 py-2">v{r.version}</td>
                    <td className="px-3 py-2 text-xs text-[#64748B]">{r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 text-xs text-[#64748B]">{r.updated_by || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ── Editor ───────────────────────────────────────────────────────────
function PageEditor({ mode, contentId }) {
  const nav = useNavigate();
  const [doc, setDoc] = useState({
    content_type: 'page', title: '', slug: '', content_html: '', visibility: 'public', seo_index: true,
    seo: {}, article: { tags: [] }, status: 'draft', version: 1,
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [sourceMode, setSourceMode] = useState(false);
  const editorRef = useRef(null);
  const initialisedRef = useRef(false);

  useEffect(() => {
    if (mode === 'edit' && contentId) {
      fetch(`${API}/admin/cms/content/${contentId}`, { credentials: 'include' })
        .then(r => r.json()).then(d => { setDoc({ seo_index: true, seo: {}, article: {}, ...d, article: d.article || {} }); setSlugTouched(true); });
    }
  }, [mode, contentId]);

  // Push loaded HTML into the contentEditable exactly once.
  useEffect(() => {
    if (!editorRef.current || sourceMode) return;
    if (!initialisedRef.current && doc.content_html) {
      editorRef.current.innerHTML = doc.content_html;
      initialisedRef.current = true;
    }
  }, [doc.content_html, sourceMode]);

  const set = (k, v) => setDoc(p => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setDoc(p => ({ ...p, seo: { ...(p.seo || {}), [k]: v } }));
  const setArticle = (k, v) => setDoc(p => ({ ...p, article: { ...(p.article || {}), [k]: v } }));

  // Slug suggestion from title (only while user hasn't manually edited slug).
  useEffect(() => {
    if (mode === 'create' && !slugTouched && doc.title) set('slug', slugify(doc.title));
    // eslint-disable-next-line
  }, [doc.title]);

  const slugConflict = doc.content_type === 'page' && isReservedSlug(doc.slug);

  // Canonical URL tracking. Auto-populate on slug/content-type change unless
  // the Platform Admin has manually overridden it. On edit we start locked
  // (respecting whatever's already saved); users unlock by clearing the
  // field or by explicitly editing it, at which point we stop overwriting.
  const [canonicalOverridden, setCanonicalOverridden] = useState(false);
  useEffect(() => {
    if (canonicalOverridden) return;
    const nextUrl = publicUrlFor(doc.content_type, doc.slug);
    const cur = doc.seo?.canonical_url || '';
    if (!nextUrl) return;
    // Only update when it differs, so we don't churn state each render.
    if (cur !== nextUrl) setSeo('canonical_url', nextUrl);
    // eslint-disable-next-line
  }, [doc.slug, doc.content_type, canonicalOverridden]);

  // If we're editing an existing doc and the saved canonical_url doesn't
  // match the current slug's derived URL, treat it as an admin override.
  useEffect(() => {
    if (mode !== 'edit') return;
    const derived = publicUrlFor(doc.content_type, doc.slug);
    const saved = doc.seo?.canonical_url || '';
    if (saved && derived && saved !== derived) setCanonicalOverridden(true);
    // eslint-disable-next-line
  }, [doc.content_id]);

  const publicUrl = publicUrlFor(doc.content_type, doc.slug);
  const publicPath = publicPathFor(doc.content_type, doc.slug);
  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public URL copied');
    } catch { toast.error('Copy failed'); }
  };

  const collectHtml = () => {
    if (sourceMode) return doc.content_html;
    return editorRef.current ? editorRef.current.innerHTML : doc.content_html;
  };

  const buildPayload = () => ({
    content_type: doc.content_type,
    title:        doc.title,
    slug:         doc.slug,
    content_html: collectHtml(),
    visibility:   doc.visibility,
    seo:          doc.seo || {},
    article:      doc.content_type === 'article' ? (doc.article || {}) : undefined,
    seo_index:    doc.seo_index !== false,
  });

  const save = async () => {
    if (!doc.title.trim()) { toast.error('Title is required'); return; }
    if (!doc.slug.trim())  { toast.error('Slug is required'); return; }
    if (slugConflict)      { toast.error(`"/${doc.slug}" is a reserved VOX route`); return; }
    setSaving(true);
    try {
      const url = mode === 'create' ? `${API}/admin/cms/content`
                                    : `${API}/admin/cms/content/${doc.content_id}`;
      const method = mode === 'create' ? 'POST' : 'PUT';
      const r = await fetch(url, { method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(buildPayload()) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Save failed'); return; }
      toast.success('Saved');
      if (mode === 'create') nav(`/admin/pages/${d.content_id}`, { replace: true });
      else setDoc({ seo_index: true, seo: {}, article: {}, ...d, article: d.article || {} });
    } finally { setSaving(false); }
  };
  const doAction = async (action) => {
    if (!doc.content_id) { toast.error('Save first'); return; }
    const r = await fetch(`${API}/admin/cms/content/${doc.content_id}/${action}`,
      { method: 'POST', credentials: 'include' });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Action failed'); return; }
    setDoc({ seo_index: true, seo: {}, article: {}, ...d, article: d.article || {} });
    toast.success(action === 'publish' ? 'Published' : action === 'archive' ? 'Archived' : action === 'new-version' ? 'New version started' : 'Done');
  };
  const doDelete = async () => {
    if (!doc.content_id) return;
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    const r = await fetch(`${API}/admin/cms/content/${doc.content_id}`, { method: 'DELETE', credentials: 'include' });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || 'Delete failed'); return; }
    toast.success('Draft deleted');
    nav('/admin/pages');
  };

  const exec = (cmd, val) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  const insertLink = () => {
    const url = window.prompt('Link URL:'); if (!url) return; exec('createLink', url);
  };
  const insertImage = async () => {
    // Reuse the existing survey-builder image endpoint so featured/inline
    // images live on the same object storage the rest of VOX uses.
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/jpeg,image/png,image/webp';
    inp.onchange = async () => {
      const f = inp.files?.[0]; if (!f) return;
      const fd = new FormData(); fd.append('file', f);
      const r = await fetch(`${API}/surveys/builder/upload-image`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Upload failed'); return; }
      const alt = window.prompt('Alternative text (recommended for accessibility):', '') || '';
      exec('insertHTML', `<img src="${d.image_url}" alt="${alt.replace(/"/g,'&quot;')}" />`);
    };
    inp.click();
  };
  const insertYouTube = () => {
    const url = window.prompt('Paste a YouTube URL:'); if (!url) return;
    exec('insertHTML', `<p>${url}</p>`);
    toast.info('YouTube URL will be embedded on save.');
  };
  const insertTable = () => {
    const rows = parseInt(window.prompt('Rows (2–20):', '3'), 10);
    const cols = parseInt(window.prompt('Cols (2–8):', '3'), 10);
    if (!(rows > 1 && cols > 1)) return;
    let h = '<table><thead><tr>' + Array.from({length: cols}).map(()=>'<th>Head</th>').join('') + '</tr></thead><tbody>';
    for (let i = 1; i < rows; i++) h += '<tr>' + Array.from({length: cols}).map(()=>'<td>Cell</td>').join('') + '</tr>';
    h += '</tbody></table>';
    exec('insertHTML', h);
  };

  const toolbarBtn = (Icon, cb, label, testid) => (
    <button type="button" onClick={cb} title={label} data-testid={testid}
            className="h-8 w-8 rounded hover:bg-[#F1F5F9] text-[#475569] inline-flex items-center justify-center">
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => nav('/admin/pages')}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <h1 className="font-['Montserrat'] text-xl font-bold">{mode === 'create' ? 'New content' : `Edit · v${doc.version} · ${doc.status}`}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
              <Field label="Content type">
                <Select value={doc.content_type} onValueChange={(v) => set('content_type', v)} disabled={mode==='edit'}>
                  <SelectTrigger data-testid="cms-content-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTENT_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Title" required>
                <Input value={doc.title} onChange={(e) => set('title', e.target.value)} data-testid="cms-title" />
              </Field>
              <Field label="Slug" required helper={slugConflict ? `"/${doc.slug}" is a reserved VOX route` : undefined}>
                <Input value={doc.slug} onChange={(e) => { setSlugTouched(true); set('slug', slugify(e.target.value)); }}
                       className={slugConflict ? 'border-rose-400' : ''} data-testid="cms-slug" />
                {publicUrl ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#475569]" data-testid="cms-public-url-row">
                    <span className="text-[#94A3B8]">Public URL:</span>
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[12px] text-[#1A3E6F] hover:underline break-all"
                      data-testid="cms-public-url-link"
                    >{publicUrl}</a>
                    <button
                      type="button"
                      onClick={copyPublicUrl}
                      className="inline-flex items-center gap-1 text-[11px] text-[#334155] hover:text-[#0F172A] border border-[#CBD5E1] rounded px-1.5 py-0.5"
                      data-testid="cms-public-url-copy"
                      title="Copy public URL"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-[#334155] hover:text-[#0F172A] border border-[#CBD5E1] rounded px-1.5 py-0.5"
                      data-testid="cms-public-url-open"
                      title="Open public URL"
                    >
                      <ExternalLink className="w-3 h-3" /> Open
                    </a>
                  </div>
                ) : null}
              </Field>
            </div>

            <div className="bg-white rounded-xl border border-[#E2E8F0]">
              <div className="flex flex-wrap items-center gap-1 border-b border-[#E2E8F0] p-2">
                <select onChange={(e) => { exec('formatBlock', e.target.value); e.target.value=''; }}
                        className="h-8 px-2 text-xs border border-[#CBD5E1] rounded" defaultValue="" data-testid="cms-tb-format">
                  <option value="">Paragraph…</option>
                  <option value="p">Paragraph</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
                {toolbarBtn(Bold,      () => exec('bold'),      'Bold',       'cms-tb-bold')}
                {toolbarBtn(Italic,    () => exec('italic'),    'Italic',     'cms-tb-italic')}
                {toolbarBtn(Underline, () => exec('underline'), 'Underline',  'cms-tb-u')}
                {toolbarBtn(Heading1,  () => exec('formatBlock','h1'), 'H1', 'cms-tb-h1')}
                {toolbarBtn(Heading2,  () => exec('formatBlock','h2'), 'H2', 'cms-tb-h2')}
                {toolbarBtn(Heading3,  () => exec('formatBlock','h3'), 'H3', 'cms-tb-h3')}
                {toolbarBtn(List,       () => exec('insertUnorderedList'), 'Bulleted list', 'cms-tb-ul')}
                {toolbarBtn(ListOrdered,() => exec('insertOrderedList'),   'Numbered list', 'cms-tb-ol')}
                {toolbarBtn(Quote,     () => exec('formatBlock','blockquote'), 'Quote', 'cms-tb-quote')}
                {toolbarBtn(Minus,     () => exec('insertHorizontalRule'), 'Divider', 'cms-tb-hr')}
                {toolbarBtn(Link2,     insertLink,   'Link',    'cms-tb-link')}
                {toolbarBtn(ImgIcon,   insertImage,  'Image',   'cms-tb-img')}
                {toolbarBtn(TableIcon, insertTable,  'Table',   'cms-tb-table')}
                {toolbarBtn(Youtube,   insertYouTube,'YouTube', 'cms-tb-yt')}
                <button type="button" onClick={() => { if (!sourceMode) set('content_html', editorRef.current?.innerHTML || ''); setSourceMode(v => !v); initialisedRef.current = !sourceMode; }}
                        className={`ml-auto text-xs px-2 h-8 rounded ${sourceMode ? 'bg-[#0F172A] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid="cms-tb-source">
                  <Code className="w-4 h-4 inline mr-1" /> Source
                </button>
              </div>
              {!sourceMode ? (
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="cms-body min-h-[360px] px-4 py-3 focus:outline-none prose-slate max-w-none"
                  data-testid="cms-editor"
                />
              ) : (
                <Textarea
                  rows={16}
                  className="rounded-none border-0 focus-visible:ring-0 font-mono text-xs"
                  value={doc.content_html}
                  onChange={(e) => set('content_html', e.target.value)}
                  placeholder="<p>Write HTML here…</p>"
                  data-testid="cms-source"
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
              <Field label="Visibility">
                <Select value={doc.visibility} onValueChange={(v) => set('visibility', v)}>
                  <SelectTrigger data-testid="cms-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBILITY.map(v => <SelectItem key={v.v} value={v.v}>{v.l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {doc.visibility === 'public' && (
                <Field label="Search-engine indexing">
                  <Select value={doc.seo_index !== false ? 'yes' : 'no'} onValueChange={(v) => set('seo_index', v === 'yes')}>
                    <SelectTrigger data-testid="cms-index"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Index (default)</SelectItem>
                      <SelectItem value="no">No index</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button className="bg-[#1A3E6F] hover:bg-[#12305A] text-white" onClick={save} disabled={saving} data-testid="cms-save">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setShowPreview(true)} data-testid="cms-preview">
                  <Eye className="w-4 h-4 mr-1.5" /> Preview
                </Button>
                {doc.content_id && doc.status !== 'published' && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doAction('publish')} data-testid="cms-publish">
                    <Send className="w-4 h-4 mr-1.5" /> Publish
                  </Button>
                )}
                {doc.content_id && doc.status === 'published' && (
                  <Button variant="outline" onClick={() => doAction('new-version')} data-testid="cms-new-version">
                    <GitBranch className="w-4 h-4 mr-1.5" /> New version
                  </Button>
                )}
                {doc.content_id && doc.status !== 'archived' && doc.published_at && (
                  <Button variant="outline" onClick={() => doAction('archive')} data-testid="cms-archive">
                    <Archive className="w-4 h-4 mr-1.5" /> Archive
                  </Button>
                )}
                {doc.content_id && !doc.published_at && (
                  <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={doDelete} data-testid="cms-delete">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                )}
              </div>
            </div>

            {doc.content_type === 'article' && (
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
                <div className="text-xs font-semibold text-[#334155]">Article fields</div>
                <Field label="Author"><Input value={doc.article?.author || ''} onChange={(e) => setArticle('author', e.target.value)} data-testid="cms-article-author" /></Field>
                <Field label="Publication date"><Input type="date" value={(doc.article?.publication_date || '').slice(0, 10)} onChange={(e) => setArticle('publication_date', e.target.value)} data-testid="cms-article-date" /></Field>
                <Field label="Featured image URL"><Input value={doc.article?.featured_image || ''} onChange={(e) => setArticle('featured_image', e.target.value)} placeholder="/api/surveys/builder/image/…" data-testid="cms-article-image" /></Field>
                <Field label="Excerpt"><Textarea rows={3} value={doc.article?.excerpt || ''} onChange={(e) => setArticle('excerpt', e.target.value)} data-testid="cms-article-excerpt" /></Field>
                <Field label="Category"><Input value={doc.article?.category || ''} onChange={(e) => setArticle('category', e.target.value)} data-testid="cms-article-category" /></Field>
                <Field label="Tags (comma-separated)">
                  <Input value={(doc.article?.tags || []).join(', ')}
                    onChange={(e) => setArticle('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                    data-testid="cms-article-tags" />
                </Field>
              </div>
            )}

            <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-3">
              <div className="text-xs font-semibold text-[#334155]">SEO</div>
              <Field label="SEO title"><Input value={doc.seo?.seo_title || ''} onChange={(e) => setSeo('seo_title', e.target.value)} data-testid="cms-seo-title" /></Field>
              <Field label="Meta description"><Textarea rows={3} value={doc.seo?.meta_description || ''} onChange={(e) => setSeo('meta_description', e.target.value)} data-testid="cms-seo-description" /></Field>
              <Field
                label="Canonical URL"
                helper={canonicalOverridden ? 'Manually overridden — will not follow slug changes.' : 'Auto-defaults to the full public URL. Edit to override.'}
              >
                <div className="flex items-center gap-2">
                  <Input
                    value={doc.seo?.canonical_url || ''}
                    onChange={(e) => { setCanonicalOverridden(true); setSeo('canonical_url', e.target.value); }}
                    data-testid="cms-seo-canonical"
                    placeholder={publicUrl || 'https://…'}
                  />
                  {canonicalOverridden && publicUrl ? (
                    <button
                      type="button"
                      className="text-[11px] text-[#1A3E6F] hover:underline whitespace-nowrap"
                      onClick={() => { setCanonicalOverridden(false); setSeo('canonical_url', publicUrl); }}
                      data-testid="cms-canonical-reset"
                    >Reset</button>
                  ) : null}
                </div>
              </Field>
              <Field label="Social title"><Input value={doc.seo?.social_title || ''} onChange={(e) => setSeo('social_title', e.target.value)} data-testid="cms-seo-social-title" /></Field>
              <Field label="Social description"><Textarea rows={2} value={doc.seo?.social_description || ''} onChange={(e) => setSeo('social_description', e.target.value)} data-testid="cms-seo-social-desc" /></Field>
              <Field label="Social image URL"><Input value={doc.seo?.social_image || ''} onChange={(e) => setSeo('social_image', e.target.value)} data-testid="cms-seo-social-image" /></Field>
            </div>
          </div>
        </div>

        {doc.versions?.length > 0 && (
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
            <div className="text-xs font-semibold text-[#334155] mb-2">Version history</div>
            <ul className="text-sm space-y-1">
              {doc.versions.map(v => (
                <li key={v.version} className="flex flex-wrap gap-3 text-[#475569]">
                  <span>v{v.version}</span>
                  <span className="text-xs">{v.published_at ? new Date(v.published_at).toLocaleString() : '—'}</span>
                  <span className="text-xs opacity-70">by {v.published_by || '—'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Preview dialog — reuses the exact public renderer so what you see
          matches what visitors would see once published. */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="p-4 border-b border-[#E2E8F0]">
            <DialogTitle>Preview — <span className="font-normal text-sm text-[#64748B]">draft, not indexed</span></DialogTitle>
          </DialogHeader>
          <div className="preview-frame">
            <PublicPage previewDoc={{ ...doc, content_html: collectHtml(), status: 'published' }} slugOverride={doc.slug} />
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Field({ label, required, helper, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-[#475569] mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {helper && <p className="text-[10.5px] text-[#64748B] mt-1">{helper}</p>}
    </div>
  );
}
